// src/pages/PlanDayDetailPage.js
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../api";
import "../App.css";
import "./PlanDayDetailPage.css";
import ExercisePicker from "../components/ExercisePicker";
import PlanDayDetailHeader from "../components/PlanDayDetailHeader";
import "../components/PlanDayDetailHeader.css";
import { ExerciseCard, BreakCard, EmptyPlanCard } from "../components/ExerciseCard";
import {
  FiCheck as CheckIcon,
  FiPlus as PlusIcon,
  FiEdit2 as EditIcon,
  FiTrash2 as TrashIcon,
} from "react-icons/fi";

function isYmd(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));
}

function titleFromYmd(ymd) {
  if (!isYmd(ymd)) return "Plan day";
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);

  return dt.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function NodeShell({
  children,
  done = false,
  showPlusTop = false,
  showPlusBottom = false,
  onPlusTop,
  onPlusBottom,
  bounded = false,
  forcePlus = false,
}) {
  return (
    <div className={`plan-flow-row ${forcePlus ? "force-plus" : ""}`}>
      <div className={`plan-flow-rail ${done ? "is-done" : ""}`}>
        <div className="plan-flow-dot" />

        {showPlusTop ? (
          <button
            type="button"
            className="plan-plus-btn plan-plus-btn--top"
            onClick={onPlusTop}
            aria-label="Add step at start"
            title="Add step at start"
          >
            <PlusIcon />
          </button>
        ) : null}

        {showPlusBottom ? (
          <button
            type="button"
            className="plan-plus-btn plan-plus-btn--bottom"
            onClick={onPlusBottom}
            aria-label="Add step"
            title="Add step"
          >
            <PlusIcon />
          </button>
        ) : null}
      </div>

      <div className={`plan-flow-node ${bounded ? "plan-flow-node--bounded" : ""}`}>
        {children}
      </div>
    </div>
  );
}

/** ✅ Outer wrapper (Repeat × plannedSets) + Done + Edit + Remove */
function RepeatBlock({
  count,
  done = false,
  onToggleDone,
  onEdit,
  onRemove,
  disabled = false,
  isEditing = false,
  children,
}) {
  const safeCount = count != null && !Number.isNaN(Number(count)) ? Number(count) : 1;

  return (
    <div className={`plan-repeat-box glass ${done ? "is-done" : ""}`}>
      <div className="plan-repeat-header">
        <span className="plan-repeat-badge">Repeat ×{safeCount}</span>

        <div className="plan-repeat-actions">
          {isEditing ? (
            <>
              <button
                type="button"
                className="plan-mini-btn"
                onClick={onEdit}
                disabled={disabled}
                aria-label="Edit"
                title="Edit"
              >
                <EditIcon />
              </button>

              <button
                type="button"
                className="plan-mini-btn danger"
                onClick={onRemove}
                disabled={disabled}
                aria-label="Remove"
                title="Remove"
              >
                <TrashIcon />
              </button>
            </>
          ) : null}

          {/* ✅ Hide Done button while editing */}
          {!isEditing ? (
            <button
              type="button"
              className={`plan-done-btn ${done ? "is-done" : ""}`}
              aria-pressed={done ? "true" : "false"}
              onClick={onToggleDone}
              disabled={disabled}
              title="Done"
            >
              <CheckIcon />
            </button>
          ) : null}
        </div>
      </div>

      {children}
    </div>
  );
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export default function PlanDayDetailPage({ user, onLogout }) {
  const { ymd } = useParams();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rawExercises, setRawExercises] = useState([]);
  const [error, setError] = useState("");

  const [doneByExercise, setDoneByExercise] = useState(() => ({}));
  const [savingByExercise, setSavingByExercise] = useState(() => ({}));

  const [dayType, setDayType] = useState(null);
  const [markDayBusy, setMarkDayBusy] = useState(false);
  const [editMode, setEditMode] = useState(0);

  const [saveBusy, setSaveBusy] = useState(false);
  const [pickerSaving, setPickerSaving] = useState(false);

  const [editingItem, setEditingItem] = useState(null);
  const [pickerAt, setPickerAt] = useState(null);

  // ✅ ref to the currently opened picker wrapper
  const pickerRootRef = useRef(null);

  // ✅ used by wheel/touch scroll swallow logic (MUST be top-level hook)
  const touchStartYRef = useRef(null);

  useEffect(() => {
    if (!editMode) {
      setPickerAt(null);
      setEditingItem(null);
    }
  }, [editMode]);

  async function loadDay() {
    if (!isYmd(ymd)) throw new Error("Invalid date in URL (expected YYYY-MM-DD).");

    const dayRes = await api(`/me/plan/${ymd}`, { cacheTtlMs: 0 });
    const workouts = dayRes?.workouts || [];

    try {
      const sched = await api("/me/plan/schedule", { cacheTtlMs: 5 * 60_000 });
      const entry = sched?.schedule?.[ymd];
      setDayType(entry?.dayType || (workouts.length ? "custom" : "rest"));
    } catch {
      setDayType(workouts.length ? "full" : "rest");
    }

    const mapped = workouts.map((w) => ({
      id: w.itemId ?? w.workoutItemId ?? `${w.id}-${Math.random()}`,
      exerciseId: w.id,

      name: w.name,
      muscleGroupId: w.muscleGroupId,
      muscleGroup: w.muscleGroup ?? "",
      reps: w.reps ?? null,
      weight: w.weight ?? null,
      plannedSets: w.plannedSets ?? 1,
      exerciseOrder: w.exerciseOrder ?? null,
      restSeconds: w.restSeconds != null ? Number(w.restSeconds) : 120,
      done: Boolean(w.done),
    }));

    setRawExercises(mapped);

    const initDone = {};
    for (const ex of mapped) initDone[ex.id] = Boolean(ex.done);
    setDoneByExercise(initDone);

    setSavingByExercise({});
  }

  async function removeItem(itemId) {
    if (!isYmd(ymd) || !itemId) return;

    setError("");
    setSavingByExercise((prev) => ({ ...(prev || {}), [itemId]: true }));

    try {
      await api(`/me/plan/${ymd}/item/${itemId}`, {
        method: "DELETE",
        invalidatePrefixes: ["/me/plan/schedule", `/me/plan/${ymd}`],
      });

      await loadDay();
    } catch (e) {
      setError(e?.message || "Failed to remove item.");
    } finally {
      setSavingByExercise((prev) => ({ ...(prev || {}), [itemId]: false }));
    }
  }

  function setEditModeAndCleanup(v) {
    setEditMode(v);
    if (!v) {
      setPickerAt(null);
      setEditingItem(null);
    }
  }

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError("");
      try {
        await loadDay();
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "Failed to load planned day.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ymd]);

  useEffect(() => {
    document.body.classList.add("no-wallpaper");
    return () => document.body.classList.remove("no-wallpaper");
  }, []);

  const items = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: "base" });
    return [...(rawExercises || [])].sort((a, b) => {
      const ao = a.exerciseOrder ?? 999999;
      const bo = b.exerciseOrder ?? 999999;
      if (ao !== bo) return ao - bo;
      return collator.compare(String(a.name || ""), String(b.name || ""));
    });
  }, [rawExercises]);

  const totalSets = useMemo(() => {
    return (items || []).reduce((sum, ex) => sum + (Number(ex.plannedSets) || 0), 0);
  }, [items]);

  const doneSets = useMemo(() => {
    return (items || []).reduce((sum, ex) => {
      const exDone = Boolean(doneByExercise?.[ex.id]);
      return sum + (exDone ? (Number(ex.plannedSets) || 0) : 0);
    }, 0);
  }, [items, doneByExercise]);

  const typeLabel = useMemo(() => {
    const t = String(dayType || "").trim();
    if (!t) return totalSets > 0 ? "Full" : "Rest";
    return t.charAt(0).toUpperCase() + t.slice(1);
  }, [dayType, totalSets]);

  const prevRectsRef = useRef(new Map());

  useLayoutEffect(() => {
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const rows = Array.from(document.querySelectorAll(".plan-flow-row"));
    const next = new Map(rows.map((el) => [el, el.getBoundingClientRect()]));
    const prev = prevRectsRef.current;

    if (!reduce && prev.size) {
      for (const el of rows) {
        const a = prev.get(el);
        const b = next.get(el);
        if (!a || !b) continue;

        const dx = a.left - b.left;
        const dy = a.top - b.top;

        if (dx || dy) {
          el.animate(
            [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "translate(0, 0)" }],
            { duration: 220, easing: "cubic-bezier(.2,.8,.2,1)" }
          );
        }
      }
    }

    prevRectsRef.current = next;
  }, [editMode, items.length]);

  async function toggleDone(itemId) {
    const current = Boolean(doneByExercise?.[itemId]);
    const next = !current;

    setDoneByExercise((prev) => ({ ...(prev || {}), [itemId]: next }));
    setSavingByExercise((prev) => ({ ...(prev || {}), [itemId]: true }));
    setError("");

    try {
      await api(`/me/plan/${ymd}/item/${itemId}/done`, {
        method: "PUT",
        body: { done: next },
      });
    } catch (e) {
      setDoneByExercise((prev) => ({ ...(prev || {}), [itemId]: current }));
      setError(e?.message || "Failed to update done state.");
    } finally {
      setSavingByExercise((prev) => ({ ...(prev || {}), [itemId]: false }));
    }
  }

  async function savePlanChanges() {
    if (!isYmd(ymd)) return;

    setSaveBusy(true);
    setError("");

    try {
      const exercises = (items || []).map((ex, idx) => ({
        itemId: ex.id,
        exerciseId: ex.exerciseId,
        sets: Number(ex.plannedSets) || 1,
        reps: ex.reps ?? 8,
        weight: null,
        restSeconds: ex.restSeconds ?? 120,
        dayType: dayType ?? "custom",
        exerciseOrder: ex.exerciseOrder ?? (idx + 1) * 100,
      }));

      await api(`/me/plan/${ymd}`, {
        method: "PUT",
        body: { exercises },
        invalidatePrefixes: ["/me/plan/schedule", `/me/plan/${ymd}`],
      });

      setEditMode(0);
      await loadDay();
    } catch (e) {
      setError(e?.message || "Failed to save plan changes.");
    } finally {
      setSaveBusy(false);
    }
  }

  async function markWholeDayDone() {
    if (!isYmd(ymd)) return;

    setMarkDayBusy(true);
    setError("");

    const prev = doneByExercise;
    const next = {};
    for (const ex of items) next[ex.id] = true;
    setDoneByExercise((cur) => ({ ...(cur || {}), ...next }));

    try {
      await api(`/me/plan/${ymd}/done`, {
        method: "PUT",
        body: { done: true },
      });
    } catch (e) {
      setDoneByExercise(prev || {});
      setError(e?.message || "Failed to mark day as done.");
    } finally {
      setMarkDayBusy(false);
    }
  }

  async function replanDay(fromYmd, toYmd) {
    const res = await api("/me/plan/replan-day", {
      method: "PUT",
      body: { fromYmd, toYmd },
      invalidatePrefixes: ["/me/plan/schedule", "/me/plan/"],
    });

    if (!res?.ok) throw new Error(res?.message || "Failed to reschedule day.");
    nav(`/plan/${toYmd}`);
  }

  function openPicker(seamKey) {
    setPickerAt(seamKey);
    setEditingItem(null);
    if (!editMode) setEditMode(1);
  }

  function openEditItem(ex) {
    setEditMode(1);

    setEditingItem({
      itemId: ex.id,
      exerciseId: ex.exerciseId,
      name: ex.name,
      sets: Number(ex.plannedSets) || 1,
      reps: Number(ex.reps) || 8,
      weight: ex.weight != null ? Number(ex.weight) : 0,
      restSeconds: ex.restSeconds != null ? Number(ex.restSeconds) : 120,
    });

    setPickerAt(`edit-${ex.id}`);
  }

  function closePicker() {
    setPickerAt(null);
    setEditingItem(null);
  }

  async function savePicker(payload) {
    if (!isYmd(ymd)) return;
    if (!payload || payload.type !== "exercise") return;

    const exerciseId = payload.exercise?.id;
    const sets = Number(payload.sets) || 1;
    const reps = Number(payload.reps) || 8;
    const weight = Number(payload.weight) || 0;

    const restSeconds =
      payload.restSeconds != null && !Number.isNaN(Number(payload.restSeconds))
        ? Math.max(0, Number(payload.restSeconds))
        : 120;

    if (!exerciseId) return;

    const isEdit = Boolean(payload?.itemId);
    const seamKey = pickerAt;

    const getInsertAt = () => {
      if (!seamKey) return null;

      if (seamKey === "start") return 1;
      if (seamKey === "end") return (items?.length || 0) + 1;

      if (String(seamKey).startsWith("after-ex-")) {
        const afterId = String(seamKey).replace("after-ex-", "");
        const idx = (items || []).findIndex((x) => x.id === afterId);
        return idx >= 0 ? idx + 2 : (items?.length || 0) + 1;
      }

      return (items?.length || 0) + 1;
    };

    const insertAt = getInsertAt();

    setPickerSaving(true);
    setError("");

    try {
      const setRows = Array.from({ length: sets }, (_, i) => ({
        setIndex: i + 1,
        reps,
        weight,
      }));

      await api(`/me/plan/${ymd}/item`, {
        method: "PUT",
        body: {
          exerciseId,
          itemId: isEdit ? payload.itemId : undefined,
          sets: setRows,
          restSeconds,
          insertAt: isEdit ? undefined : insertAt,
        },
        invalidatePrefixes: ["/me/plan/schedule", `/me/plan/${ymd}`],
      });

      await loadDay();
      closePicker();
    } catch (e) {
      setError(e?.message || "Failed to save exercise.");
      throw e;
    } finally {
      setPickerSaving(false);
    }
  }

  // ✅ lock page scroll while picker open (prevents page scrolling behind wheel/touch)
  useEffect(() => {
    const isOpen = Boolean(pickerAt);
    if (!isOpen) return;

    const body = document.body;
    const html = document.documentElement;

    const prevOverflow = body.style.overflow;
    const prevPosition = body.style.position;
    const prevTop = body.style.top;
    const prevWidth = body.style.width;

    const scrollY = window.scrollY || window.pageYOffset || 0;

    // iOS-safe scroll lock (position: fixed)
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    html.style.overscrollBehaviorY = "none";

    return () => {
      // restore
      body.style.overflow = prevOverflow;
      body.style.position = prevPosition;
      body.style.top = prevTop;
      body.style.width = prevWidth;
      html.style.overscrollBehaviorY = "";

      const y = Math.abs(parseInt(body.style.top || "0", 10)) || scrollY;
      window.scrollTo(0, y);
    };
  }, [pickerAt]);

  // ✅ stop scroll chaining: if picker is open, swallow wheel/touch that starts outside picker
  useEffect(() => {
    if (!pickerAt) return;

    const isInsidePicker = (target) => {
      const root = pickerRootRef.current;
      if (!root) return false;
      return root.contains(target);
    };

    const onWheel = (e) => {
      if (!pickerAt) return;
      if (isInsidePicker(e.target)) return; // allow internal scrolling
      e.preventDefault();
    };

    const onTouchStart = (e) => {
      if (!pickerAt) return;
      touchStartYRef.current = e.touches?.[0]?.clientY ?? null;
    };

    const onTouchMove = (e) => {
      if (!pickerAt) return;
      if (isInsidePicker(e.target)) return; // allow internal scrolling
      e.preventDefault();
    };

    // capture + non-passive so preventDefault works
    window.addEventListener("wheel", onWheel, { passive: false, capture: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false, capture: true });

    return () => {
      window.removeEventListener("wheel", onWheel, { capture: true });
      window.removeEventListener("touchstart", onTouchStart, { capture: true });
      window.removeEventListener("touchmove", onTouchMove, { capture: true });
    };
  }, [pickerAt]);

  function renderPicker(seamKey) {
    if (pickerAt !== seamKey) return null;

    const isEditSeam = String(seamKey).startsWith("edit-");

    return (
      <div data-picker-anchor={seamKey} ref={pickerRootRef}>
        <NodeShell done={false} bounded>
          <ExercisePicker
            onClose={closePicker}
            onSave={savePicker}
            busy={pickerSaving}
            mode={isEditSeam ? "edit" : "add"}
            itemId={isEditSeam ? editingItem?.itemId || null : null}
            initialExercise={
              isEditSeam && editingItem
                ? { id: editingItem.exerciseId, name: editingItem.name }
                : null
            }
            initialSets={isEditSeam ? editingItem?.sets ?? 3 : 3}
            initialReps={isEditSeam ? editingItem?.reps ?? 8 : 8}
            initialWeight={isEditSeam ? editingItem?.weight ?? 0 : 20}
            initialRestSeconds={isEditSeam ? editingItem?.restSeconds ?? 120 : 120}
          />
        </NodeShell>
      </div>
    );
  }

  function renderInlineEditPicker(itemId) {
    const seamKey = `edit-${itemId}`;
    if (pickerAt !== seamKey) return null;

    return (
      <div data-picker-anchor={seamKey} ref={pickerRootRef}>
        <ExercisePicker
          onClose={closePicker}
          onSave={savePicker}
          busy={pickerSaving}
          mode="edit"
          itemId={editingItem?.itemId || null}
          initialExercise={editingItem ? { id: editingItem.exerciseId, name: editingItem.name } : null}
          initialSets={editingItem?.sets ?? 3}
          initialReps={editingItem?.reps ?? 8}
          initialWeight={editingItem?.weight ?? 0}
          initialRestSeconds={editingItem?.restSeconds ?? 120}
        />
      </div>
    );
  }

  // ✅ memoized scroll helpers
  const getScrollParent = useCallback((el) => {
    let p = el?.parentElement;
    while (p) {
      const style = window.getComputedStyle(p);
      const overflowY = style.overflowY;
      const canScroll =
        (overflowY === "auto" || overflowY === "scroll") &&
        p.scrollHeight > p.clientHeight + 1;

      if (canScroll) return p;
      p = p.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }, []);

  const scrollToCenter = useCallback(
    (el, behavior = "smooth") => {
      if (!el) return;

      const scroller = getScrollParent(el);
      const elRect = el.getBoundingClientRect();

      const scRect = scroller.getBoundingClientRect
        ? scroller.getBoundingClientRect()
        : { top: 0, height: window.innerHeight };

      const isPageScroller =
        scroller === document.scrollingElement || scroller === document.documentElement;

      const elTopInScroller = isPageScroller
        ? elRect.top + (window.scrollY || window.pageYOffset || 0)
        : elRect.top - scRect.top + scroller.scrollTop;

      const target = elTopInScroller - (scRect.height / 2 - elRect.height / 2);

      if (isPageScroller) {
        window.scrollTo({ top: Math.max(0, target), behavior });
      } else {
        scroller.scrollTo({ top: Math.max(0, target), behavior });
      }
    },
    [getScrollParent]
  );

  useLayoutEffect(() => {
    if (!pickerAt) return;

    const behavior = prefersReducedMotion() ? "auto" : "smooth";

    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-picker-anchor="${pickerAt}"]`);
        if (!el) return;
        scrollToCenter(el, behavior);
      });
    });

    return () => cancelAnimationFrame(raf1);
  }, [pickerAt, scrollToCenter]);

  return (
    <>
      <div className="plan-day-gradient">
        <Navbar
          user={user}
          onLogout={onLogout}
          tab="plan"
          showBack
          backTo="/plan"
          title={`Your plan for ${titleFromYmd(ymd)}`}
        />

        <div className="page-wrapper">
          <div className="page-container">
            <div className="plan-header-sentinel" />

            <div className="plan-header glass">
              <PlanDayDetailHeader
                ymd={ymd}
                typeLabel={typeLabel}
                doneSets={doneSets}
                totalSets={totalSets}
                isEditing={Boolean(editMode)}
                onEditPlan={setEditModeAndCleanup}
                onSavePlan={savePlanChanges}
                saveBusy={saveBusy}
                onMarkDayDone={markWholeDayDone}
                markDayBusy={markDayBusy}
                onReplanDay={replanDay}
              />
            </div>

            <main className="plan-stack">
              {loading ? (
                <div className="card glass">
                  <div className="muted">Loading…</div>
                </div>
              ) : error ? (
                <div className="card glass">
                  <div className="error-text">{error}</div>
                </div>
              ) : items.length === 0 ? (
                <div className={`plan-flow ${editMode || pickerAt ? "is-editing" : ""}`}>
                  <NodeShell
                    done={false}
                    bounded
                    forcePlus={true}
                    showPlusBottom={true}
                    onPlusBottom={() => openPicker("start")}
                  >
                    <EmptyPlanCard />
                  </NodeShell>

                  {renderPicker("start")}
                </div>
              ) : (
                <div className={`plan-flow ${editMode ? "is-editing" : ""}`}>
                  {renderPicker("start")}

                  {items.map((x, idx) => {
                    const isFirstExercise = idx === 0;
                    const isLastExercise = idx === items.length - 1;

                    const done = Boolean(doneByExercise?.[x.id]);
                    const saving = Boolean(savingByExercise?.[x.id]);

                    const afterExerciseKey = isLastExercise ? "end" : `after-ex-${x.id}`;
                    const showBreak = (Number(x.plannedSets) || 1) > 1;

                    return (
                      <Fragment key={`item-${x.id}`}>
                        <NodeShell
                          done={done}
                          showPlusTop={Boolean(editMode) && isFirstExercise}
                          showPlusBottom={Boolean(editMode)}
                          onPlusTop={() => openPicker("start")}
                          onPlusBottom={() => openPicker(afterExerciseKey)}
                        >
                          <RepeatBlock
                            count={x.plannedSets}
                            done={done}
                            disabled={saving}
                            isEditing={Boolean(editMode)}
                            onToggleDone={() => toggleDone(x.id)}
                            onEdit={() => openEditItem(x)}
                            onRemove={() => removeItem(x.id)}
                          >
                            {pickerAt === `edit-${x.id}` ? (
                              <div className="plan-node-card plan-node-card--gutterless glass">
                                {renderInlineEditPicker(x.id)}
                              </div>
                            ) : (
                              <>
                                <ExerciseCard
                                  name={x.name}
                                  muscleGroupId={x.muscleGroupId}
                                  muscleGroup={x.muscleGroup}
                                  reps={x.reps}
                                />
                                {showBreak ? <BreakCard restSeconds={x.restSeconds} /> : null}
                              </>
                            )}
                          </RepeatBlock>
                        </NodeShell>

                        {renderPicker(afterExerciseKey)}
                      </Fragment>
                    );
                  })}
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </>
  );
}